import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTabPanel } from "react-aria";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";
import { ActivityBar, StateActivityBar } from "./ActivityBar";
import { SideBar } from "./SideBar";

import type { ReactElement, ReactNode } from "react";
import type { SidebarTabsState } from "../../hooks/useSidebarTabsState";
import type { ActivityBarItem } from "./ActivityBar";
import type { ViewDefinition } from "./activityTypes";

// ─────────────────────────────────────────────────────────────────────────────
// Helper to map ViewDefinition to ActivityBarItem
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a ViewDefinition to an ActivityBarItem (extracts only what the ActivityBar needs).
 */
function viewToItem(view: ViewDefinition): ActivityBarItem {
  return {
    id: view.id,
    icon: view.icon,
    label: view.label,
    badge: view.badge,
    badgeColor: view.badgeColor,
  };
}

export interface SidebarLayoutProps {
  /** Available views for the ActivityBar + SideBar. */
  views: ViewDefinition[];
  /** Views anchored at the bottom of the activity bar (e.g., settings). */
  bottomViews?: ViewDefinition[];
  /** Currently active view id. If provided, component is controlled. */
  activeViewId?: string | null;
  /** Callback when view is selected. */
  onViewSelect?: (id: string) => void;
  /** Width of the sidebar when open. */
  sidebarWidth?: number;
  className?: string;
  /** Optional class name for the ActivityBar. */
  activityBarClassName?: string;
  /** Optional class name for the SideBar. */
  sideBarClassName?: string;
  /**
   * Whether the sidebar is collapsed.
   *
   * If provided (true or false), the component is in **controlled mode**.
   * You must provide `onCollapseChange` to handle updates.
   *
   * If undefined, the component is in **uncontrolled mode** and manages its own collapse state.
   */
  isCollapsed?: boolean;
  /**
   * Callback when collapse state changes.
   * Required when `isCollapsed` is provided.
   */
  onCollapseChange?: (collapsed: boolean) => void;
  /**
   * If true, all view panels stay mounted when switching tabs.
   * This preserves component state and prevents re-renders.
   * Default: false (only active view is mounted)
   */
  keepMounted?: boolean;
}

/**
 * Example layout wiring ActivityBar + SideBar together.
 *
 * - Left: ActivityBar (vertical icon rail)
 * - Right: SideBar (collapsible panel showing the active view's component)
 * - Clicking the active icon toggles the sidebar open/closed.
 */
export function SidebarLayout({
  views,
  bottomViews,
  activeViewId: controlledActiveViewId,
  onViewSelect,
  sidebarWidth = 360,
  className,
  activityBarClassName,
  sideBarClassName,
  isCollapsed: controlledIsCollapsed,
  onCollapseChange,
  keepMounted = false,
}: SidebarLayoutProps): ReactElement {
  const [internalActiveViewId, setInternalActiveViewId] = useState<string | null>(
    views[0]?.id ?? null
  );

  const activeViewId =
    controlledActiveViewId !== undefined ? controlledActiveViewId : internalActiveViewId;

  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const isCollapsed =
    controlledIsCollapsed !== undefined ? controlledIsCollapsed : internalIsCollapsed;

  // Combine all views for lookup
  const allViews = useMemo(() => [...views, ...(bottomViews ?? [])], [views, bottomViews]);

  const activeView = useMemo(
    () => allViews.find(view => view.id === activeViewId) ?? null,
    [allViews, activeViewId]
  );

  // Map views to ActivityBarItems for the ActivityBar
  const activityBarItems = useMemo(() => views.map(viewToItem), [views]);
  const activityBarBottomItems = useMemo(() => bottomViews?.map(viewToItem), [bottomViews]);

  const handleViewSelect = useCallback(
    (id: string) => {
      if (activeViewId === id) {
        // Clicking the active icon toggles collapse
        const newCollapsed = !isCollapsed;
        if (controlledIsCollapsed === undefined) {
          setInternalIsCollapsed(newCollapsed);
        }
        onCollapseChange?.(newCollapsed);
        return;
      }

      if (isCollapsed) {
        if (controlledIsCollapsed === undefined) {
          setInternalIsCollapsed(false);
        }
        onCollapseChange?.(false);
      }

      if (controlledActiveViewId === undefined) {
        setInternalActiveViewId(id);
      }
      onViewSelect?.(id);
    },
    [
      activeViewId,
      isCollapsed,
      controlledIsCollapsed,
      controlledActiveViewId,
      onCollapseChange,
      onViewSelect,
    ]
  );

  const isSidebarOpen = !!activeView && !isCollapsed;

  const handleCollapse = useCallback(() => {
    if (controlledIsCollapsed === undefined) {
      setInternalIsCollapsed(true);
    }
    onCollapseChange?.(true);
  }, [controlledIsCollapsed, onCollapseChange]);

  const collapseIcon = useMemo(
    () => (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9.0031 4.51715V15.4829H15.9813C16.2554 15.4829 16.4797 15.2586 16.4797 14.9844V5.01559C16.4797 4.74145 16.2554 4.51715 15.9813 4.51715H9.0031ZM2.0249 5.01559C2.0249 3.9159 2.91898 3.02182 4.01867 3.02182H15.9813C17.081 3.02182 17.9751 3.9159 17.9751 5.01559V14.9844C17.9751 16.0841 17.081 16.9782 15.9813 16.9782H4.01867C2.91898 16.9782 2.0249 16.0841 2.0249 14.9844V5.01559ZM4.01867 5.76325C4.01867 6.17758 4.35201 6.51092 4.76634 6.51092H6.26166C6.67599 6.51092 7.00933 6.17758 7.00933 5.76325C7.00933 5.34892 6.67599 5.01559 6.26166 5.01559H4.76634C4.35201 5.01559 4.01867 5.34892 4.01867 5.76325ZM4.76634 8.00624C4.35201 8.00624 4.01867 8.33958 4.01867 8.75391C4.01867 9.16824 4.35201 9.50157 4.76634 9.50157H6.26166C6.67599 9.50157 7.00933 9.16824 7.00933 8.75391C7.00933 8.33958 6.67599 8.00624 6.26166 8.00624H4.76634ZM4.01867 11.7446C4.01867 12.1589 4.35201 12.4922 4.76634 12.4922H6.26166C6.67599 12.4922 7.00933 12.1589 7.00933 11.7446C7.00933 11.3302 6.67599 10.9969 6.26166 10.9969H4.76634C4.35201 10.9969 4.01867 11.3302 4.01867 11.7446Z"
          fill="currentColor"
        />
      </svg>
    ),
    []
  );

  return (
    <div className={cn("flex h-full", className)}>
      <ActivityBar
        items={activityBarItems}
        bottomItems={activityBarBottomItems}
        activeItemId={isSidebarOpen ? activeViewId : null}
        onItemSelect={handleViewSelect}
        className={activityBarClassName}
      />

      <SideBar isOpen={isSidebarOpen} width={sidebarWidth} className={sideBarClassName}>
        {/* Header with title + collapse button */}
        {activeView && !activeView.hideHeader && (
          <div className="flex h-9 shrink-0 items-center justify-between border-b border-gray-200 pl-4 pr-3 dark:border-gray-700">
            <div className="select-none text-sm font-semibold text-gray-900 dark:text-gray-50">
              {activeView.header ?? activeView.label}
            </div>
            <Button
              startContent={collapseIcon}
              onPress={handleCollapse}
              variant="light"
              size="sm"
              isIconOnly
              aria-label="Collapse sidebar"
            />
          </div>
        )}

        {/* Panel content */}
        {keepMounted ? (
          // Keep all panels mounted, only show active one
          <div className="relative min-h-0 flex-1 overflow-hidden">
            {activeView?.hideHeader && (
              <div className="absolute right-3 top-1.5 z-20">
                <Button
                  startContent={collapseIcon}
                  onPress={handleCollapse}
                  variant="light"
                  size="sm"
                  isIconOnly
                  aria-label="Collapse sidebar"
                />
              </div>
            )}
            {allViews.map(view => {
              const isActive = view.id === activeViewId;
              const ViewComponent = view.component;
              return (
                <div
                  key={view.id}
                  className={cn(
                    "absolute inset-0 overflow-auto bg-layer-layer1",
                    isActive ? "z-10 opacity-100" : "pointer-events-none z-0 opacity-0"
                  )}
                  aria-hidden={!isActive}
                >
                  <ViewComponent />
                </div>
              );
            })}
          </div>
        ) : (
          // Only mount active view (original behavior)
          activeView && (
            <div className="relative min-h-0 flex-1 overflow-auto">
              {activeView.hideHeader && (
                <div className="absolute right-3 top-1.5 z-20">
                  <Button
                    startContent={collapseIcon}
                    onPress={handleCollapse}
                    variant="light"
                    size="sm"
                    isIconOnly
                    aria-label="Collapse sidebar"
                  />
                </div>
              )}
              <activeView.component />
            </div>
          )
        )}
      </SideBar>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State-based SidebarLayout with react-aria accessibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extended ActivityBarItem that includes a component for rendering.
 */
export interface SidebarViewItem extends ActivityBarItem {
  /** Component to render in the sidebar panel. */
  component: React.ComponentType;
  /** Optional custom header. If not provided, label is used. */
  header?: ReactNode;
  /**
   * If true, the default header is not rendered, allowing the component
   * to provide its own header or go headerless.
   */
  hideHeader?: boolean;
}

export interface StateSidebarLayoutProps<T extends SidebarViewItem> {
  /** The tab state from useSidebarTabsState. */
  state: SidebarTabsState<T>;
  /** Width of the sidebar when open. */
  sidebarWidth?: number;
  /** Custom className for the layout container. */
  className?: string;
  /** Custom className for the ActivityBar. */
  activityBarClassName?: string;
  /** Custom className for the SideBar. */
  sideBarClassName?: string;
  /** ARIA label for the tab list. */
  "aria-label"?: string;
}

/**
 * State-based SidebarLayout that uses useSidebarTabsState for state management.
 * Provides full react-aria accessibility.
 *
 * @example
 * ```tsx
 * const state = useSidebarTabsState({
 *   children: views.map(v => <Item key={v.id}>{v}</Item>),
 *   defaultSelectedKey: "incidents",
 * });
 *
 * <StateSidebarLayout state={state} />
 * ```
 */
export function StateSidebarLayout<T extends SidebarViewItem>({
  state,
  sidebarWidth = 360,
  className,
  activityBarClassName,
  sideBarClassName,
  "aria-label": ariaLabel = "Sidebar Navigation",
}: StateSidebarLayoutProps<T>): ReactElement {
  const tabPanelRef = useRef<HTMLDivElement>(null);

  // Get tab panel props from react-aria for accessibility
  const { tabPanelProps } = useTabPanel({}, state, tabPanelRef);

  // Get the selected item from the collection
  const selectedItem = useMemo(() => {
    if (!state.selectedKey) return null;
    const item = state.collection.getItem(state.selectedKey);
    return item?.value ?? null;
  }, [state.collection, state.selectedKey]);

  // Determine if sidebar should be open
  const isSidebarOpen = !state.isCollapsed && state.selectedKey !== null;

  const handleCollapse = useCallback(() => {
    state.setCollapsed(true);
  }, [state]);

  const collapseIcon = useMemo(
    () => (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9.0031 4.51715V15.4829H15.9813C16.2554 15.4829 16.4797 15.2586 16.4797 14.9844V5.01559C16.4797 4.74145 16.2554 4.51715 15.9813 4.51715H9.0031ZM2.0249 5.01559C2.0249 3.9159 2.91898 3.02182 4.01867 3.02182H15.9813C17.081 3.02182 17.9751 3.9159 17.9751 5.01559V14.9844C17.9751 16.0841 17.081 16.9782 15.9813 16.9782H4.01867C2.91898 16.9782 2.0249 16.0841 2.0249 14.9844V5.01559ZM4.01867 5.76325C4.01867 6.17758 4.35201 6.51092 4.76634 6.51092H6.26166C6.67599 6.51092 7.00933 6.17758 7.00933 5.76325C7.00933 5.34892 6.67599 5.01559 6.26166 5.01559H4.76634C4.35201 5.01559 4.01867 5.34892 4.01867 5.76325ZM4.76634 8.00624C4.35201 8.00624 4.01867 8.33958 4.01867 8.75391C4.01867 9.16824 4.35201 9.50157 4.76634 9.50157H6.26166C6.67599 9.50157 7.00933 9.16824 7.00933 8.75391C7.00933 8.33958 6.67599 8.00624 6.26166 8.00624H4.76634ZM4.01867 11.7446C4.01867 12.1589 4.35201 12.4922 4.76634 12.4922H6.26166C6.67599 12.4922 7.00933 12.1589 7.00933 11.7446C7.00933 11.3302 6.67599 10.9969 6.26166 10.9969H4.76634C4.35201 10.9969 4.01867 11.3302 4.01867 11.7446Z"
          fill="currentColor"
        />
      </svg>
    ),
    []
  );

  return (
    <div className={cn("flex h-full", className)}>
      <StateActivityBar state={state} className={activityBarClassName} aria-label={ariaLabel} />

      <SideBar isOpen={isSidebarOpen} width={sidebarWidth} className={sideBarClassName}>
        {/* Header with title + collapse button */}
        {selectedItem && !selectedItem.hideHeader && (
          <div className="flex h-9 shrink-0 items-center justify-between border-b border-gray-200 pl-4 pr-3 dark:border-gray-700">
            <div className="select-none text-sm font-semibold text-gray-900 dark:text-gray-50">
              {selectedItem.header ?? selectedItem.label}
            </div>
            <Button
              startContent={collapseIcon}
              onPress={handleCollapse}
              variant="light"
              size="sm"
              isIconOnly
              aria-label="Collapse sidebar"
            />
          </div>
        )}

        {/* Panel content */}
        <div {...tabPanelProps} ref={tabPanelRef} className="relative min-h-0 flex-1 overflow-auto">
          {selectedItem?.hideHeader && (
            <div className="absolute right-3 top-1.5 z-20">
              <Button
                startContent={collapseIcon}
                onPress={handleCollapse}
                variant="light"
                size="sm"
                isIconOnly
                aria-label="Collapse sidebar"
              />
            </div>
          )}
          {selectedItem && <selectedItem.component />}
        </div>
      </SideBar>
    </div>
  );
}
